import { useState } from "react";
import { Fingerprint, Lock, Grid3X3 } from "lucide-react";
import { verifyEsignUnlock } from "../../lib/esign/api";
import { hashPattern, hashUnlockPassword } from "../../lib/esign/crypto";
import { loadEsignLocal } from "../../lib/esign/local-store";
import { PatternLock } from "./PatternLock";

type Props = {
  apiBase: string;
  userId: string;
  busy?: boolean;
  onUnlocked: () => void;
  onCancel?: () => void;
};

/**
 * Unlock with password (required on web), optional pattern / WebAuthn MVP.
 */
export function CertUnlockPanel({
  apiBase,
  userId,
  busy,
  onUnlocked,
  onCancel,
}: Props) {
  const local = loadEsignLocal(userId);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [showPattern, setShowPattern] = useState(false);

  const unlockWithHash = async (input: {
    unlockPasswordHash?: string;
    patternHash?: string;
  }) => {
    setWorking(true);
    setError(null);
    try {
      // Prefer local compare first (spec), then confirm with stub API.
      if (input.unlockPasswordHash && local?.unlockPasswordHash) {
        if (input.unlockPasswordHash !== local.unlockPasswordHash) {
          throw new Error("رمز امضا نادرست است");
        }
      }
      if (input.patternHash && local?.patternHash) {
        if (input.patternHash !== local.patternHash) {
          throw new Error("الگوی باز کردن نادرست است");
        }
      }
      await verifyEsignUnlock(apiBase, input);
      onUnlocked();
    } catch (e) {
      setError(e instanceof Error ? e.message : "باز کردن ناموفق بود");
    } finally {
      setWorking(false);
    }
  };

  const tryWebAuthn = async () => {
    if (!window.PublicKeyCredential) {
      setError("این مرورگر از اثرانگشت / WebAuthn پشتیبانی نمی‌کند");
      return;
    }
    setWorking(true);
    setError(null);
    try {
      // MVP: presence-only WebAuthn — preference flag must be enabled.
      if (!local?.fingerprintEnabled) {
        throw new Error("اثرانگشت برای این حساب فعال نشده است");
      }
      const challenge = crypto.getRandomValues(new Uint8Array(32));
      await navigator.credentials.get({
        publicKey: {
          challenge,
          timeout: 60_000,
          userVerification: "required",
          rpId: window.location.hostname,
        },
      });
      onUnlocked();
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message.includes("NotAllowed")
            ? "احراز بیومتریک لغو شد"
            : e.message
          : "WebAuthn ناموفق بود — از رمز استفاده کنید",
      );
    } finally {
      setWorking(false);
    }
  };

  if (showPattern) {
    return (
      <div className="signing-auth-panel">
        {error ? <p className="signing-error">{error}</p> : null}
        <PatternLock
          mode="unlock"
          onCancel={() => setShowPattern(false)}
          onComplete={(dots) => {
            void hashPattern(dots).then((patternHash) =>
              unlockWithHash({ patternHash }),
            );
          }}
        />
      </div>
    );
  }

  return (
    <div className="signing-auth-panel">
      <p className="signing-muted">
        برای امضای گواهی‌محور، رمز امضا یا روش باز کردن را وارد کنید.
      </p>
      {error ? <p className="signing-error">{error}</p> : null}
      <div className="signing-form">
        <label className="signing-field">
          رمز امضا
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="رمز تعیین‌شده در احراز هویت"
          />
        </label>
        <button
          type="button"
          className="primary-btn"
          disabled={busy || working || password.length < 4}
          onClick={() => {
            void hashUnlockPassword(password).then((unlockPasswordHash) =>
              unlockWithHash({ unlockPasswordHash }),
            );
          }}
        >
          <Lock size={16} aria-hidden />
          {working ? "در حال بررسی…" : "باز کردن و ادامه"}
        </button>
        {local?.patternEnabled ? (
          <button
            type="button"
            className="ghost-btn"
            disabled={busy || working}
            onClick={() => setShowPattern(true)}
          >
            <Grid3X3 size={16} aria-hidden />
            باز کردن با الگو
          </button>
        ) : null}
        {local?.fingerprintEnabled ? (
          <button
            type="button"
            className="ghost-btn"
            disabled={busy || working}
            onClick={() => void tryWebAuthn()}
          >
            <Fingerprint size={16} aria-hidden />
            اثرانگشت / بیومتریک
          </button>
        ) : null}
        {onCancel ? (
          <button type="button" className="ghost-btn" onClick={onCancel}>
            انصراف
          </button>
        ) : null}
      </div>
    </div>
  );
}
